"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  ArrowRight,
  Play,
  Zap,
  Shield,
  BarChart3,
  Users,
  Code,
  Brain,
  Target,
  TrendingUp,
  Clock,
  Award,
  Settings,
  Globe,
  Lock,
  FileText,
  Video,
  MessageSquare,
  PieChart,
  Map,
  Star,
  Check,
  Minus,
  Plus,
  Sparkles,
} from "lucide-react";

import {
  PiUser,
  PiUsers,
  PiChalkboardTeacher,
  PiGraduationCap,
} from "react-icons/pi";
import { useDynamicLogo } from "@/hooks/use-dynamic-logo";



interface LandingPageProps {
  className?: string;
  subdomain?: string;
}

export default function LandingPage({ className = "", subdomain }: LandingPageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [subdomainRole, setSubdomainRole] = useState<string | null>(null);
  const { logo } = useDynamicLogo();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);



  const navLinks = [
    { label: "Product", href: "#product" },
    { label: "Features", href: "#features" },
    { label: "Pricing", href: "#pricing" },
    { label: "Resources", href: "#resources" },
  ];

  const platformFeatures = [
    { icon: <FileText className="w-5 h-5" />, title: "Assessments", color: "bg-blue-100 text-blue-600" },
    { icon: <Code className="w-5 h-5" />, title: "Coding", color: "bg-emerald-100 text-emerald-600" },
    { icon: <Brain className="w-5 h-5" />, title: "AI Evaluation", color: "bg-violet-100 text-violet-600" },
    { icon: <BarChart3 className="w-5 h-5" />, title: "Analytics", color: "bg-amber-100 text-amber-600" },
    { icon: <Video className="w-5 h-5" />, title: "Proctoring", color: "bg-rose-100 text-rose-600" },
    { icon: <MessageSquare className="w-5 h-5" />, title: "Collaboration", color: "bg-cyan-100 text-cyan-600" },
  ];

  const whyInstacks = [
    { icon: <Target className="w-5 h-5" />, title: "Precision", desc: "AI-driven evaluation", color: "bg-blue-100 text-blue-600" },
    { icon: <TrendingUp className="w-5 h-5" />, title: "Scale", desc: "Handle thousands", color: "bg-emerald-100 text-emerald-600" },
    { icon: <Zap className="w-5 h-5" />, title: "Speed", desc: "Automated workflows", color: "bg-amber-100 text-amber-600" },
    { icon: <Settings className="w-5 h-5" />, title: "Flexibility", desc: "Fully customizable", color: "bg-violet-100 text-violet-600" },
    { icon: <PieChart className="w-5 h-5" />, title: "Insights", desc: "Data-driven decisions", color: "bg-cyan-100 text-cyan-600" },
    { icon: <Shield className="w-5 h-5" />, title: "Security", desc: "Enterprise-grade", color: "bg-rose-100 text-rose-600" },
  ];

  const features = [
    { icon: <Code className="w-4 h-4" />, title: "Coding Tests", color: "text-emerald-600" },
    { icon: <FileText className="w-4 h-4" />, title: "MCQ Tests", color: "text-blue-600" },
    { icon: <Brain className="w-4 h-4" />, title: "AI Proctoring", color: "text-violet-600" },
    { icon: <BarChart3 className="w-4 h-4" />, title: "Reports", color: "text-amber-600" },
    { icon: <Settings className="w-4 h-4" />, title: "Test Builder", color: "text-cyan-600" },
    { icon: <Users className="w-4 h-4" />, title: "Candidates", color: "text-blue-600" },
    { icon: <Video className="w-4 h-4" />, title: "Video Response", color: "text-rose-600" },
    { icon: <Lock className="w-4 h-4" />, title: "Secure", color: "text-emerald-600" },
    { icon: <Globe className="w-4 h-4" />, title: "Web-Based", color: "text-cyan-600" },
    { icon: <Clock className="w-4 h-4" />, title: "Timed Tests", color: "text-amber-600" },
    { icon: <Award className="w-4 h-4" />, title: "Certificates", color: "text-violet-600" },
    { icon: <Sparkles className="w-4 h-4" />, title: "AI Matching", color: "text-blue-600" },
  ];

  const dsTopics = [
    { title: "Arrays", difficulty: 35, color: "bg-emerald-500" },
    { title: "Linked Lists", difficulty: 45, color: "bg-amber-500" },
    { title: "Trees", difficulty: 60, color: "bg-orange-500" },
    { title: "Graphs", difficulty: 75, color: "bg-rose-500" },
    { title: "Dynamic Prog.", difficulty: 80, color: "bg-rose-500" },
    { title: "Stacks", difficulty: 40, color: "bg-amber-500" },
    { title: "Hash Tables", difficulty: 45, color: "bg-amber-500" },
    { title: "Heaps", difficulty: 55, color: "bg-orange-500" },
  ];

  const languages = ["Python", "Java", "JavaScript", "C++", "C#", "Go"];

  const stakeholders = [
    { title: "Students", desc: "Practice & get hired", color: "bg-blue-100" },
    { title: "Recruiters", desc: "Find top talent", color: "bg-emerald-100" },
    { title: "Colleges", desc: "Track placements", color: "bg-violet-100" },
    { title: "Companies", desc: "Streamline hiring", color: "bg-amber-100" },
    { title: "Admins", desc: "Manage platform", color: "bg-cyan-100" },
  ];

  const stats = [
    { value: "2M+", label: "Users", color: "text-blue-600" },
    { value: "1L+", label: "Tests", color: "text-emerald-600" },
    { value: "80M+", label: "Submissions", color: "text-violet-600" },
    { value: "99.9%", label: "Uptime", color: "text-amber-600" },
  ];

  const testimonials = [
    { quote: "Reduced screening time by 70% and found better matches.", name: "Priya S.", role: "IIT Delhi" },
    { quote: "Students get placed faster with better suited companies.", name: "Rajesh K.", role: "BITS Pilani" },
    { quote: "Finding quality candidates has never been easier.", name: "Anita P.", role: "TCS" },
  ];

  const faqs = [
    { q: "How does AI evaluation work?", a: "ML models analyze code for correctness, efficiency, and quality with instant scoring." },
    { q: "Can I customize assessments?", a: "Yes, fully customize question types, rubrics, time limits, and thresholds." },
    { q: "Is the platform secure?", a: "Yes, enterprise-grade encryption, AI proctoring, GDPR and SOC2 compliant." },
  ];

  return (
    <div className={`min-h-screen bg-white text-gray-900 antialiased ${className}`}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all ${isScrolled ? "bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm" : "bg-transparent"}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="flex items-center gap-2">
              {logo && <img src={logo} alt="Instacks" className="h-9 w-auto object-contain" />}
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((l) => (
                <Link key={l.label} href={l.href} className="text-sm text-gray-500 hover:text-blue-600 transition-colors">{l.label}</Link>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-3">
              <Link
                href="/login"
                className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
              >
                Sign in
              </Link>
              <button className="bg-blue-600 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors">Get Started</button>
            </div>
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden py-3 border-t border-gray-100">
              <nav className="flex flex-col gap-1">
                {navLinks.map((l) => (
                  <Link key={l.label} href={l.href} className="text-sm text-gray-600 hover:text-blue-600 px-3 py-2 rounded-lg hover:bg-gray-50">{l.label}</Link>
                ))}
                <div className="flex gap-2 pt-2 px-3">
                  <Link href="/login" className="flex-1 text-center text-sm font-medium text-gray-600 border border-gray-200 py-2 rounded-lg">Sign in</Link>
                  <button className="flex-1 bg-blue-600 text-white text-sm font-medium py-2 rounded-lg">Get Started</button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
                <Sparkles className="w-3.5 h-3.5" />
                AI-Powered Hiring Platform
              </div>
              <h1 className="text-4xl md:text-5xl font-semibold text-gray-900 leading-tight mb-5">
                The Future of <span className="text-blue-600">Campus Hiring</span>
              </h1>
              <p className="text-lg text-gray-500 mb-8 leading-relaxed">
                Transform your hiring with intelligent assessments, real-time analytics, and seamless candidate management.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button className="bg-blue-600 text-white text-sm font-semibold px-6 py-3.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                  Get Started Free <ArrowRight className="w-4 h-4" />
                </button>
                <button className="text-gray-600 text-sm font-semibold px-6 py-3.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                  <Play className="w-4 h-4" /> Watch Demo
                </button>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                </div>
                <span className="text-xs text-gray-400">Dashboard</span>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <div className="text-2xl font-bold text-blue-600">2,847</div>
                    <div className="text-xs text-blue-600/70 mt-1">Candidates</div>
                  </div>
                  <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                    <div className="text-2xl font-bold text-violet-600">94%</div>
                    <div className="text-xs text-violet-600/70 mt-1">Accuracy</div>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                    <div className="text-2xl font-bold text-emerald-600">342</div>
                    <div className="text-xs text-emerald-600/70 mt-1">Placed</div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Placement Rate</span>
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2.5 py-1 rounded-full">+12%</span>
                  </div>
                  <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full w-[72%] bg-gradient-to-r from-blue-500 to-violet-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className={`text-2xl md:text-3xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-sm text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-10 px-6 border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-gray-400 text-center mb-6">Trusted by leading institutions</p>
          <div className="flex flex-wrap items-center justify-center gap-10 opacity-40">
            {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-6 w-28 bg-gray-300 rounded"></div>)}
          </div>
        </div>
      </section>

      {/* Platform */}
      <section id="product" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Explore the Platform</h2>
            <p className="text-base text-gray-500">Everything you need to assess and hire top talent</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {platformFeatures.map((f, i) => (
              <div key={i} className="group p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all cursor-pointer text-center">
                <div className={`w-12 h-12 ${f.color.split(' ')[0]} rounded-xl flex items-center justify-center mx-auto mb-3 ${f.color.split(' ')[1]} group-hover:scale-110 transition-transform`}>
                  {f.icon}
                </div>
                <h3 className="text-sm font-medium text-gray-900">{f.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Instacks */}
      <section className="py-16 px-6 bg-gradient-to-b from-blue-50/30 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Why Instacks?</h2>
            <p className="text-base text-gray-500">Built for modern hiring challenges</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyInstacks.map((w, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all">
                <div className={`w-12 h-12 ${w.color.split(' ')[0]} rounded-xl flex items-center justify-center mb-4 ${w.color.split(' ')[1]}`}>
                  {w.icon}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">{w.title}</h3>
                <p className="text-sm text-gray-500">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Everything You Need</h2>
            <p className="text-base text-gray-500">Powerful tools for comprehensive assessments</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-3.5 p-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition cursor-pointer group">
                <div className={`w-10 h-10 bg-gray-50 group-hover:bg-gray-100 rounded-lg flex items-center justify-center ${f.color}`}>
                  {f.icon}
                </div>
                <span className="text-sm font-medium text-gray-900">{f.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problems */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-violet-50/20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">Every Problem, One Platform</h2>
              <p className="text-base text-gray-500 mb-6 leading-relaxed">Stop juggling multiple tools. Instacks consolidates your entire hiring workflow.</p>
              <div className="space-y-3">
                {["Manual screening processes", "Lack of real-time insights", "Fragmented communication", "No standardized metrics"].map((p, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center"><Minus className="w-3 h-3 text-rose-600" /></div>
                    {p}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                </div>
                <span className="text-xs text-gray-400">Candidates</span>
              </div>
              <div className="p-6">
                <div className="space-y-3 mb-4">
                  {[{ name: "Rahul Sharma", score: 92, status: "Shortlisted" }, { name: "Priya Patel", score: 88, status: "Reviewing" }, { name: "Amit Kumar", score: 76, status: "Pending" }].map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-3.5 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-violet-500 rounded-full flex items-center justify-center text-white text-sm font-medium">{c.name.charAt(0)}</div>
                        <span className="text-sm font-medium text-gray-900">{c.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">{c.score}%</span>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${c.status === "Shortlisted" ? "bg-emerald-100 text-emerald-700" : c.status === "Reviewing" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{c.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3.5 bg-blue-50 rounded-lg border border-blue-100"><div className="text-lg font-bold text-blue-600">92%</div><div className="text-xs text-blue-600/70 mt-0.5">Accuracy</div></div>
                  <div className="text-center p-3.5 bg-emerald-50 rounded-lg border border-emerald-100"><div className="text-lg font-bold text-emerald-600">2.4s</div><div className="text-xs text-emerald-600/70 mt-0.5">Avg Time</div></div>
                  <div className="text-center p-3.5 bg-violet-50 rounded-lg border border-violet-100"><div className="text-lg font-bold text-violet-600">340</div><div className="text-xs text-violet-600/70 mt-0.5">Today</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DSA */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Data Structures & Algorithms</h2>
            <p className="text-base text-gray-500">Master essential topics for placements</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dsTopics.map((t, i) => (
              <div key={i} className="bg-gray-50 p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition cursor-pointer">
                <h3 className="text-base font-medium text-gray-900 mb-3">{t.title}</h3>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${t.color}`} style={{ width: `${t.difficulty}%` }}></div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded ${t.difficulty < 50 ? "bg-emerald-100 text-emerald-700" : t.difficulty < 70 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{t.difficulty < 50 ? "Easy" : t.difficulty < 70 ? "Medium" : "Hard"}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Languages */}
      <section className="py-16 px-6 bg-gray-50/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Programming Languages</h2>
            <p className="text-base text-gray-500">Multi-language support for hiring needs</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {languages.map((l, i) => (
              <div key={i} className="bg-white p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-center">
                <div className="text-base font-semibold text-gray-900 mb-3">{l}</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 bg-gradient-to-r from-blue-500 to-violet-500 rounded-full" style={{ width: "88%" }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mind Maps */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">Interactive Mind Maps</h2>
              <p className="text-base text-gray-500 mb-6 leading-relaxed">Visualize connections between concepts. Navigate topics seamlessly.</p>
              <div className="grid grid-cols-2 gap-2">
                {["Arrays & Strings", "Linked Lists", "Trees & Tries", "Graphs", "Dynamic Prog.", "Sorting"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5 p-3.5 bg-gray-50 rounded-lg text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition cursor-pointer border border-transparent hover:border-blue-100">
                    <Map className="w-4 h-4" />{t}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-violet-50 rounded-2xl p-8 border border-blue-100">
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-violet-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg mb-6">Core</div>
                <div className="grid grid-cols-3 gap-3 w-full">
                  {["Arrays", "Trees", "Graphs"].map((t, i) => <div key={i} className="bg-white p-3 rounded-lg text-center text-sm font-medium text-gray-700 border border-blue-100 shadow-sm">{t}</div>)}
                </div>
                <div className="flex gap-2 mt-3">
                  {["DP", "Hash", "Stack"].map((t, i) => <div key={i} className="bg-white px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-blue-100 shadow-sm">{t}</div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Code Editor */}
      <section className="py-16 px-6 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold mb-3">Code in a Professional Environment</h2>
            <p className="text-base text-gray-400">Professional IDE with syntax highlighting and real-time output</p>
          </div>
          <div className="bg-gray-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                </div>
                <span className="text-sm text-gray-400">main.py</span>
              </div>
              <div className="flex items-center gap-4">
                <select className="bg-gray-700 text-white text-sm px-3 py-1 rounded border-none"><option>Python</option></select>
                <div className="flex items-center gap-2 text-sm text-gray-400"><Clock className="w-4 h-4" />45:00</div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3">
              <div className="col-span-2 p-5 font-mono text-sm text-gray-300 overflow-x-auto">
                <pre className="leading-relaxed">{"def binary_search(arr, target):\n    left, right = 0, len(arr) - 1\n    while left <= right:\n        mid = (left + right) // 2\n        if arr[mid] == target:\n            return mid\n        elif arr[mid] < target:\n            left = mid + 1\n        else:\n            right = mid - 1\n    return -1"}</pre>
              </div>
              <div className="bg-gray-950 p-5 border-t lg:border-t-0 lg:border-l border-gray-700">
                <div className="text-sm text-gray-500 mb-3">Output</div>
                <div className="font-mono text-sm space-y-1">
                  <div className="text-emerald-400">&gt; Test 1: PASSED</div>
                  <div className="text-emerald-400">&gt; Test 2: PASSED</div>
                  <div className="text-emerald-400">&gt; Test 3: PASSED</div>
                  <div className="text-white mt-2">All tests passed!</div>
                </div>
                <button className="mt-4 w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition">Run Code</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Simple, Transparent Pricing</h2>
            <p className="text-base text-gray-500">Choose the plan that fits your needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { name: "Starter", price: "$99", period: "/month", features: ["100 tests/month", "Basic analytics", "Email support", "Up to 500 candidates"] },
              { name: "Professional", price: "$299", period: "/month", features: ["Unlimited tests", "AI insights", "Priority support", "Up to 5,000 candidates"], popular: true },
              { name: "Enterprise", price: "Custom", period: "", features: ["Unlimited everything", "Custom branding", "API access", "Dedicated manager"] },
            ].map((p, i) => (
              <div key={i} className={`relative p-6 rounded-2xl border ${p.popular ? "bg-gray-900 text-white border-gray-900" : "bg-white border-gray-200"}`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-sm font-semibold px-4 py-1 rounded-full">Most Popular</div>}
                <h3 className={`text-lg font-semibold mb-1 ${p.popular ? "text-white" : "text-gray-900"}`}>{p.name}</h3>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-3xl font-bold">{p.price}</span>
                  <span className={`text-sm ${p.popular ? "text-gray-400" : "text-gray-500"}`}>{p.period}</span>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm">
                      <Check className={`w-4 h-4 ${p.popular ? "text-blue-400" : "text-emerald-500"}`} />
                      <span className={p.popular ? "text-gray-300" : "text-gray-600"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-2.5 rounded-lg text-sm font-semibold transition ${p.popular ? "bg-white text-gray-900 hover:bg-gray-100" : "border border-gray-200 text-gray-900 hover:border-gray-300 hover:bg-gray-50"}`}>{p.popular ? "Get Started" : "Learn More"}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">How It Works</h2>
            <p className="text-base text-gray-500">Simple 6-step process to transform your hiring</p>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-10 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-violet-200 to-purple-200"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-4">
              {[
                { step: "01", title: "Create Test", icon: <FileText className="w-6 h-6" /> },
                { step: "02", title: "Invite", icon: <Users className="w-6 h-6" /> },
                { step: "03", title: "Monitor", icon: <Video className="w-6 h-6" /> },
                { step: "04", title: "Evaluate", icon: <Brain className="w-6 h-6" /> },
                { step: "05", title: "Analyze", icon: <BarChart3 className="w-6 h-6" /> },
                { step: "06", title: "Hire", icon: <Award className="w-6 h-6" /> },
              ].map((item, i) => (
                <div key={i} className="relative text-center">
                  <div className="inline-flex flex-col items-center">
                    <div className="relative z-10 w-20 h-20 bg-white border-2 border-blue-100 rounded-full flex items-center justify-center text-blue-600 shadow-lg hover:shadow-xl hover:border-blue-200 hover:scale-105 transition-all">
                      {item.icon}
                    </div>
                    <div className="mt-4">
                      <div className="text-xs font-bold text-blue-600 mb-1">{item.step}</div>
                      <div className="text-sm font-semibold text-gray-900">{item.title}</div>
                    </div>
                  </div>
                  {i < 5 && (
                    <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] text-gray-300">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Intelligence That Transforms</h2>
            <p className="text-base text-gray-500">AI-powered insights for smarter decisions</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { title: "AI Insights", desc: "ML-powered analysis identifies top candidates with 94% accuracy.", color: "bg-blue-100 text-blue-600" },
              { title: "Candidate Ranking", desc: "Intelligent ranking based on skills and cultural fit.", color: "bg-violet-100 text-violet-600" },
              { title: "Smart Analytics", desc: "Predictive analytics surface trends and recommendations.", color: "bg-emerald-100 text-emerald-600" },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all">
                <div className={`w-12 h-12 ${item.color.split(' ')[0]} rounded-xl flex items-center justify-center mb-4 ${item.color.split(' ')[1]}`}><Brain className="w-6 h-6" /></div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stakeholders */}
      <section className="py-16 px-6 bg-gradient-to-b from-violet-50/20 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Built for Everyone</h2>
            <p className="text-base text-gray-500">Tailored experience for each user</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
            {stakeholders.map((s, i) => (
              <div key={i} className="bg-white p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-center cursor-pointer">
                <div className={`w-12 h-12 ${s.color} rounded-full mx-auto mb-3 flex items-center justify-center`}><Users className="w-5 h-5 text-gray-700" /></div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{s.title}</h3>
                <p className="text-xs text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Loved by Teams</h2>
            <p className="text-base text-gray-500">See what our customers say</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 rounded-xl border border-gray-100 bg-gradient-to-b from-gray-50/50 to-white">
                <div className="flex gap-0.5 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">&quot;{t.quote}&quot;</p>
                <div><div className="text-sm font-semibold text-gray-900">{t.name}</div><div className="text-xs text-gray-500">{t.role}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 bg-gray-50/50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-3">Frequently Asked</h2>
          </div>
          <div className="space-y-2">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button className="w-full px-5 py-4 flex items-center justify-between text-left" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-sm font-medium text-gray-900">{f.q}</span>
                  {openFaq === i ? <Minus className="w-4 h-4 text-gray-400" /> : <Plus className="w-4 h-4 text-gray-400" />}
                </button>
                {openFaq === i && <div className="px-5 pb-4"><p className="text-sm text-gray-500 leading-relaxed">{f.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 via-violet-600 to-purple-700 rounded-3xl p-10 md:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-700 via-violet-700 to-purple-800"></div>
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-300 rounded-full blur-3xl"></div>
            </div>
            <div className="relative">
              <h2 className="text-3xl font-semibold text-white mb-3">Transform Your Hiring Today</h2>
              <p className="text-blue-100 mb-8 text-base max-w-xl mx-auto">Join thousands of organizations using AI-powered assessments.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button className="w-full sm:w-auto bg-white text-blue-600 px-8 py-3.5 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-sm">
                  Get Started Free <ArrowRight className="w-4 h-4" />
                </button>
                <button className="w-full sm:w-auto text-white px-8 py-3.5 rounded-lg font-medium hover:text-blue-200 transition-colors border border-white/30 flex items-center justify-center gap-2 text-sm">
                  <Play className="w-4 h-4" /> Watch Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <Link href="/" className="flex items-center gap-2.5 mb-4">
                {logo && <img src={logo} alt="Instacks" className="h-7 w-auto object-contain" />}
              </Link>
              <p className="text-sm text-gray-500 mb-4">AI-Powered Campus Hiring & Skill Development</p>
              <div className="flex gap-2">
                {["f", "t", "in"].map((s, i) => <div key={i} className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center text-sm text-gray-500 hover:bg-blue-100 hover:text-blue-600 transition cursor-pointer">{s}</div>)}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">Product</h4>
              <ul className="space-y-2.5 text-sm">
                {["Features", "Pricing", "Security", "API"].map((item, i) => <li key={i}><Link href="#" className="text-gray-500 hover:text-blue-600 transition">{item}</Link></li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">Company</h4>
              <ul className="space-y-2.5 text-sm">
                {["About", "Careers", "Blog", "Contact"].map((item, i) => <li key={i}><Link href="#" className="text-gray-500 hover:text-blue-600 transition">{item}</Link></li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">Resources</h4>
              <ul className="space-y-2.5 text-sm">
                {["Docs", "Help Center", "Community", "Webinars"].map((item, i) => <li key={i}><Link href="#" className="text-gray-500 hover:text-blue-600 transition">{item}</Link></li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-4">Legal</h4>
              <ul className="space-y-2.5 text-sm">
                {["Privacy", "Terms", "GDPR", "Cookies"].map((item, i) => <li key={i}><Link href="#" className="text-gray-500 hover:text-blue-600 transition">{item}</Link></li>)}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-8 text-center">
            <p className="text-sm text-gray-400">&copy; 2026 Instacks. A Product of BVS Education Private Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}